import { ChangeDetectionStrategy, Component, inject, linkedSignal, viewChild } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { injectParams } from 'ngxtension/inject-params';
import { LifecycleRulesetStore } from '../store/lifecycle-ruleset.store';
import { LifecycleRulesEnableService } from './lifecycle-rules-enable.service';
import { EnableEntityComponent } from '@r1-compass-library/components/enable-entity/enable-entity.component';
import { EnableSaveGuardWarning } from '@r1-compass-library/guards/save-guards/enable.guard';

@Component({
    selector: 'r1-compass-app-lifecycle-rules-enable',
    imports: [EnableEntityComponent],
    template: `
        <r1-compass-lib-enable-entity
            [currentEnableStatus]="currentEnableStatus()"
            [copyHeader]="'Enable for Play Use'"
            [entityType]="'Lifecycle Ruleset'"
            (save)="onSave()"
            (toggleChange)="onToggleChange($event)">
            Toggle “Enabled” to allow plays to use this Lifecycle Ruleset or “Disabled” to prevent this Lifecycle
            Ruleset from being used. The Lifecycle Ruleset must not be in use by any active Plays to be disabled.
        </r1-compass-lib-enable-entity>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LifecycleRulesEnableService],
})
export class LifecycleRulesEnableComponent implements EnableSaveGuardWarning {
    readonly #store = inject(LifecycleRulesetStore);
    readonly #enableService = inject(LifecycleRulesEnableService);

    protected currentEnableStatus = linkedSignal<boolean | undefined>(() => this.#store.lifecycleRuleset()?.enabled);

    public enableComponentSpy = viewChild.required(EnableEntityComponent);

    private lifecycleRulesetId = injectParams<string | null>('id');

    constructor() {
        const lifecycleRulesetId = this.lifecycleRulesetId();
        if (lifecycleRulesetId && lifecycleRulesetId !== 'new' && this.#store.lifecycleRuleset()?.id !== +lifecycleRulesetId) {
            this.#store.getLifecycleRuleset(+lifecycleRulesetId);
        }
    }

    public onSave() {
        return this.#enableService.save(this.currentEnableStatus());
    }

    protected async onToggleChange(event: MatSlideToggleChange) {
        const lifecycleRuleset = this.#store.lifecycleRuleset();
        if (!lifecycleRuleset) {
            return;
        }

        const outcome = event.checked
            ? await this.#enableService.enable()
            : await this.#enableService.disable(lifecycleRuleset);

        if (outcome.status === 'success') {
            this.currentEnableStatus.set(outcome.value.shouldSetEnabled);
            event.source.checked = outcome.value.shouldSetEnabled;
        }
    }
}
